import { time } from '@dr.pogodin/react-utils';

import type { RawArtistDocT } from 'server/models';
import { getId } from 'utils';

import type { ArtistT } from './artist';
import type { BodyAreaT } from './body-areas';
import { type BodyPositionT, xmlBodyPositions } from './body-positions';
import type { VideoIntentT } from './intents';

import {
  type DifficultyT,
  type TransformationStageT,
  xmlDifficulty,
  xmlDuration,
  xmlEmotionalMechanisms,
  xmlEnvironmentNeeds,
  xmlEquipmentNeeds,
  xmlIntendedShift,
  xmlModalities,
  xmlNeurologicalMechanisms,
  xmlPhysicalMechanisms,
  xmlTargetBodyAreas,
  xmlTitle,
  xmlTransformationStages,
} from './life-moments-framework';

import type { ModalityT } from './modalities';

import { type XmlNodeArrayT, type XmlNodeT, xmlNode } from './xml';

export const DEFAULT_VIDEO_PAGE_SIZE = 20;

export const AI_ANALYSIS_STATE = [
  'DONE',
  'META_DATA_EXTRACTION_IN_PROGRESS',
] as const;

export type AiAnalysisState = typeof AI_ANALYSIS_STATE[number];

/**
 * There are multiple components that show previews of video uploaded to Vimeo,
 * and if such videos are still being processed by Vimeo those components need
 * to periodically refresh video data, to detect once they are ready to be
 * played. This constant sets the frequence of such refreshes.
 */
export const VIDEO_STATUS_REFRESH_INTERVAL = 10 * time.SEC_MS;

export const VIDEO_STATUSES = [
  'Deleted',
  'Draft',
  'Hidden',
  'Published',
] as const;

export type VideoStatusT = typeof VIDEO_STATUSES[number];

export type VideoT = {
  aiAnalysis?: null | string;

  aiAnalysisState: AiAnalysisState | null | undefined;

  /** ID of the video author in DB. */
  artistId: string;

  bodyAreas: BodyAreaT[];
  bodyPositions: BodyPositionT[];

  /** Creation timestamp. It is filled on server-side from the time information
   *  contained within the `id` (ObjectId) field. */
  createdAt?: number;

  customBodyAreas?: null | string[];
  customEquipmentRequirements?: null | string[];
  customModalities?: null | string[];
  difficultyLevels: DifficultyT[];

  /** Video duration in minutes (set once deduced by Vimeo). */
  durationMin?: null | number;

  /**
   * NOTE: Although we have ENVIRONMENT_NEEDS list, it feels like its values
   * will change over time a lot. Thus, at least for now, let's keep this typed
   * as just a generic string in video type.
   */
  environmentNeeds?: null | string;

  // TODO: It is supposed to be EquipmentKindT[], but let's keep it as a generic
  // string until the set of values is settled.
  equipmentNeedsV2: string[];

  /** Video height (set once deduced by Vimeo). */
  height?: null | number;

  id?: string;

  /** Primary video intent. */
  intent?: VideoIntentT | null;

  /** "true" if the video has been processed by Vimeo,
   *  and it is ready to be played. */
  isReadyToBePlayed: boolean;

  modalities: ModalityT[];
  publishedAt?: null | number;
  status: VideoStatusT;
  thumbnailUrl: string;
  title?: null | string;
  url: string;
  userSummary?: null | string;

  /** Video width (set once deduced by Vimeo). */
  width?: null | number;

  // New video traits.
  // TODO: The "| undefined" pieces here are necessary just to avoid
  // breaking the frontend on non-updated video objects; remove them later.
  emotionalMechanisms: string[] | undefined;
  modalitiesV2: string[] | undefined;
  neurologicalMechanisms: string[] | undefined;
  physicalMechanisms: string[] | undefined;
  shiftFrom: string[] | undefined;
  shiftTo: string[] | undefined;

  // TODO: "undefined" possibility is temporary, until the database is patched
  // fully in all environments.
  transformationStages: TransformationStageT[] | undefined;
};

export type VideoFiltersT = {
  artistIds?: string | string[];
  bodyAreas?: BodyAreaT[];
  customModalities?: string[];
  difficulty?: DifficultyT[];

  /** Video duration range, minutes. */
  durationMin?: [number, number];

  intents?: VideoIntentT[];
  modalities?: ModalityT[];
};

export type VideoSortT = {
  artistName?: 1 | -1;
  createdAt?: 1 | -1;
  duration?: 1 | -1;
  modalities?: 1 | -1;
  publishedAt?: 1 | -1;
  status?: 1 | -1;
  title?: 1 | -1;
};

export type XmlVideoT = XmlNodeT<'video'>;

function xmlVideoAuthor(artist: ArtistT | RawArtistDocT): XmlNodeT<'author'> {
  return xmlNode('author', [
    xmlNode(
      'id',

      // eslint-disable-next-line no-underscore-dangle
      '_id' in artist ? artist._id.toString() : getId(artist),

      'Practitioner ID.',
    ),
    xmlNode('name', artist.displayName, 'Practitioner name.'),
  ], 'Video author (artist, coach, practitioner).');
}

export function xmlVideo(
  video: VideoT,
  author?: ArtistT | RawArtistDocT,
): XmlVideoT {
  const nodes: XmlNodeArrayT = [
    xmlNode('id', getId(video), 'Internal video ID'),
    xmlTitle(video.title),
  ];

  if (author) nodes.push(xmlVideoAuthor(author));

  nodes.push(
    xmlDuration(video.durationMin),
    xmlNode('intent', video.intent, 'Primary video intent.'),
    xmlTransformationStages(video.transformationStages ?? []),
    xmlModalities([
      ...video.modalitiesV2 ?? [],
      ...video.modalities,
      ...video.customModalities ?? [],
    ]),
    xmlTargetBodyAreas([
      ...video.bodyAreas,
      ...video.customBodyAreas ?? [],
    ]),
    xmlBodyPositions(video.bodyPositions),
    xmlDifficulty(video.difficultyLevels),
    xmlEnvironmentNeeds(video.environmentNeeds),
    xmlEquipmentNeeds([
      ...video.equipmentNeedsV2,
      ...video.customEquipmentRequirements ?? [],
    ]),
    xmlIntendedShift(video.shiftFrom, video.shiftTo),

    xmlEmotionalMechanisms(video.emotionalMechanisms),
    xmlNeurologicalMechanisms(video.neurologicalMechanisms),
    xmlPhysicalMechanisms(video.physicalMechanisms),
  );

  return {
    kind: 'video',
    xml: xmlNode('video_description', nodes).xml,
  };
}
